import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase/client';
import { getPasswordError } from '../utils/passwordValidation';
import { useMobileDetection } from '../hooks/useMobileDetection';
import LogoNav from '../components/Navigation/LogoNav';
import './LoginPage.css'; // Reuse login page styles

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { changePassword } = useAuth();
  const { isMobile, isTablet } = useMobileDetection();
  
  // Treat tablets as mobile for layout purposes
  const useMobileLayout = isMobile || isTablet;
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [isVerifyingCode, setIsVerifyingCode] = useState(true);

  // Handle password reset code verification
  useEffect(() => {
    const checkResetSession = async () => {
      const urlParams = new URLSearchParams(location.search);
      
      // Debug logging
      console.log('🔍 ResetPasswordPage - URL parameters:', {
        fullUrl: window.location.href,
        search: location.search,
        hasError: !!urlParams.get('error'),
        hasCode: !!urlParams.get('code'),
        hasType: !!urlParams.get('type')
      });
      
      // Check for error parameters first
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      const errorCode = urlParams.get('error_code');
      
      if (error) {
        console.log('❌ Auth callback error:', { error, errorCode, errorDescription });
        
        let friendlyMessage = 'Password reset link is invalid or has expired. Please request a new one.';
        
        if (errorCode === 'otp_expired' || errorDescription?.includes('expired')) {
          friendlyMessage = 'Your password reset link has expired. Please request a new one.';
        } else if (error === 'access_denied') {
          friendlyMessage = 'Access denied. Please request a new password reset link.';
        }
        
        setError(friendlyMessage);
        setIsValidToken(false);
        setIsVerifyingCode(false);
        return;
      }

      try {
        console.log('🔍 Checking for reset session...');
        
        // For password resets, Supabase automatically creates a session when the user clicks the link
        // We just need to check if we have a valid session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        console.log('🔍 Session check result:', {
          hasSession: !!session,
          hasUser: !!session?.user,
          userEmail: session?.user?.email,
          sessionError: sessionError?.message
        });
        
        if (sessionError) {
          console.log('❌ Session error:', sessionError);
          setError('Unable to verify password reset token. Please request a new reset link.');
          setIsValidToken(false);
        } else if (session && session.user) {
          console.log('✅ Valid reset session found for user:', session.user.email);
          setIsValidToken(true);
        } else {
          console.log('❌ No valid session found - user needs to click the reset link');
          setError('Please click the password reset link in your email to continue.');
          setIsValidToken(false);
        }
      } catch (err) {
        console.error('❌ Reset session check error:', err);
        setError('An error occurred while processing your password reset link.');
        setIsValidToken(false);
      } finally {
        setIsVerifyingCode(false);
      }
    };

    checkResetSession();
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    // Reset states
    setError(null);
    setSuccess(null);
    
    // Validation
    if (!newPassword || !confirmPassword) {
      setError('Please fill in both password fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    const passwordError = getPasswordError(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await changePassword(newPassword);
      
      if (result.success) {
        setSuccess('Your password has been updated successfully! Redirecting to sign in...');
        // Redirect to login after success
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(result.message || 'Failed to update password');
      }
    } catch (error: any) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading while verifying the reset code
  if (isVerifyingCode) {
    return (
      <div className="login-page">
        <LogoNav />
        <div className="login-container">
          <div className="login-card">
            <div className="login-logo">
              <img src="/DES-20250328_Logo_v3_SS.png" alt="ZoningPal" />
            </div>
            <div className="login-header">
              <h1>Verifying Reset Link...</h1>
              <p className="login-subtitle">Please wait while we verify your password reset link</p>
            </div>
            <div className="auth-form">
              <div className="loading-spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error if token is invalid
  if (isValidToken === false) {
    return (
      <div className="login-page">
        {!useMobileLayout && <LogoNav />}
        <div className="login-container">
          <div className="login-card">
            {!useMobileLayout && (
              <div className="login-logo">
                <img src="/DES-20250328_Logo_v3_SS.png" alt="ZoningPal" />
              </div>
            )}
            <div className="login-header">
              <h1>Reset Link Expired</h1>
              <p className="login-subtitle">Your password reset link has expired or is invalid</p>
            </div>
            
            {error && (
              <div className="auth-error" role="alert">
                <div className="error-icon">!</div>
                <div className="error-text">{error}</div>
              </div>
            )}
            
            <div className="auth-form">
              <button 
                type="button"
                onClick={() => navigate('/login')}
                className="auth-submit-button"
              >
                Go to Sign In
              </button>
              
              {/* Add option to request new reset */}
              <div className="auth-toggle">
                <span className="toggle-text">Need a new reset link?</span>
                <button 
                  onClick={() => navigate('/login?mode=forgot-password')} 
                  className="toggle-button"
                  type="button"
                >
                  Request New Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show password reset form
  return (
    <div className="login-page">
      {!useMobileLayout && <LogoNav />}
      <div className="login-container">
        <div className="login-card">
          {!useMobileLayout && (
            <div className="login-logo">
              <img src="/DES-20250328_Logo_v3_SS.png" alt="ZoningPal" />
            </div>
          )}
          
          <div className="login-header">
            <h1>Set New Password</h1>
            <p className="login-subtitle">Enter your new password to complete the reset</p>
          </div>
          
          {/* Success Message */}
          {success && (
            <div className="auth-success" role="alert">
              <div className="success-icon">✓</div>
              <div className="success-text">{success}</div>
            </div>
          )}
          
          {/* Error Message */}
          {error && (
            <div className="auth-error" role="alert">
              <div className="error-icon">!</div>
              <div className="error-text">{error}</div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="auth-form" noValidate>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Enter your new password"
                className="auth-input"
                disabled={isSubmitting}
                autoComplete="new-password"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your new password"
                className="auth-input"
                disabled={isSubmitting}
                autoComplete="new-password"
              />
              
              {/* Password match indicator */}
              {confirmPassword && newPassword && (
                <div className={`password-match-indicator ${newPassword === confirmPassword ? 'match' : 'no-match'}`}>
                  {newPassword === confirmPassword ? (
                    <div className="match-success">
                      ✓ Passwords match
                    </div>
                  ) : (
                    <div className="match-error">
                      ✗ Passwords do not match
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <button 
              type="submit" 
              className={`auth-submit-button ${isSubmitting ? 'loading' : ''}`}
              disabled={isSubmitting || !newPassword || !confirmPassword || newPassword !== confirmPassword}
            >
              {isSubmitting ? 'Updating Password...' : 'Update Password'}
            </button>
          </form>
          
          <div className="auth-toggle">
            <span className="toggle-text">Remember your password?</span>
            <button 
              onClick={() => navigate('/login')} 
              className="toggle-button"
              type="button"
              disabled={isSubmitting}
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage; 